import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ArrowLeft, TrendingUp, Users, Zap, Star, RefreshCw, ChevronUp, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Design tokens ─────────────────────────────────────────────── */
const T = {
  bg:          "#0A0907",
  card:        "rgba(255,255,255,0.032)",
  border:      "rgba(255,255,255,0.075)",
  borderGold:  "rgba(176,141,87,0.35)",
  gold:        "#B08D57",
  goldSoft:    "#E8D5A8",
  teal:        "#4FBFB0",
  sky:         "#5B91C7",
  rose:        "#C0685A",
  violet:      "#9B72CF",
  text:        "#F5EED8",
  textSub:     "#9E8F80",
  textMuted:   "#5E5248",
  grid:        "rgba(255,255,255,0.05)",
  success:     "#4FBF7A",
};

const MODEL_COLORS = {
  "claude-sonnet-4-6": T.gold,
  "claude-opus-4-6":   T.violet,
  "gpt-4o":            T.sky,
  "gpt-5.4":           T.teal,
  "gemini-3-pro":      T.success,
};
const MODEL_LABELS = {
  "claude-sonnet-4-6": "Claude Sonnet",
  "claude-opus-4-6":   "Claude Opus",
  "gpt-4o":            "GPT-4o",
  "gpt-5.4":           "GPT-5.4",
  "gemini-3-pro":      "Gemini 3 Pro",
};

const CE_COLORS = ["#C0526A","#E8A23A","#8B4FBF","#C0685A","#5B91C7","#4FBFB0","#9B72CF","#E8C23A","#4FBF7A"];
const CE_LABELS = {
  "CE-01": "Romance",  "CE-02": "Joy",      "CE-03": "Wonder",
  "CE-04": "Fury",     "CE-05": "Sorrow",   "CE-06": "Dread",
  "CE-07": "Revulsion","CE-08": "Valor",    "CE-09": "Tranquility",
};

/* ── Sub-components ─────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, sub, accent, delta }) {
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${accent ? T.borderGold : T.border}`,
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      position: "relative",
      overflow: "hidden",
    }}>
      {accent && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${T.gold}, transparent)`,
        }} />
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textSub }}>
          {label}
        </span>
        <Icon size={15} color={accent ? T.gold : T.textMuted} />
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: accent ? T.goldSoft : T.text, lineHeight: 1 }}>
        {value}
      </div>
      {(sub || delta !== undefined) && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {delta !== undefined && delta > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 11, color: T.success }}>
              <ChevronUp size={12} /> {delta} this week
            </span>
          )}
          {sub && <span style={{ fontSize: 12, color: T.textMuted }}>{sub}</span>}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
      textTransform: "uppercase", color: T.textSub,
      borderBottom: `1px solid ${T.border}`, paddingBottom: 10, marginBottom: 20,
    }}>
      {children}
    </h2>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#1A1510", border: `1px solid ${T.border}`,
      borderRadius: 10, padding: "10px 14px", fontSize: 12,
    }}>
      <p style={{ color: T.textSub, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || T.gold }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

function FunnelBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 4;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: T.textSub }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{value.toLocaleString()}</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: T.border }}>
        <div style={{
          height: "100%", borderRadius: 4,
          width: `${pct}%`,
          background: color,
          transition: "width 800ms cubic-bezier(0.4,0,0.2,1)",
        }} />
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────── */
export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setData(r.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* Admin guard */
  if (user && user.email !== "admin@arcscript.ai") {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: T.textSub }}>Access restricted.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 32, height: 32, border: `2px solid ${T.gold}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ color: T.textSub, fontSize: 13 }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <p style={{ color: T.rose, marginBottom: 12 }}>{error}</p>
          <button onClick={fetchData} style={{ color: T.gold, background: "transparent", border: `1px solid ${T.gold}`, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Retry</button>
        </div>
      </div>
    );
  }

  const { overview, daily_series, model_breakdown, score_distribution, ce_breakdown, engagement_buckets, recent_activity } = data;
  const funnelMax = overview.total_demo_ever;

  /* Format date labels */
  const formatDate = (d) => {
    const [, , day] = d.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const m = parseInt(d.split("-")[1]) - 1;
    return `${months[m]} ${parseInt(day)}`;
  };

  const seriesWithLabel = daily_series.map(d => ({ ...d, label: formatDate(d.date) }));

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${T.border}`,
        padding: "16px 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        background: T.bg,
        zIndex: 50,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={() => navigate("/studio")}
            style={{ display: "flex", alignItems: "center", gap: 6, color: T.textSub, background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>
            <ArrowLeft size={15} /> Studio
          </button>
          <div style={{ width: 1, height: 16, background: T.border }} />
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>ArcScript Analytics</span>
            <span style={{ marginLeft: 10, fontSize: 11, color: T.textMuted }}>
              Last updated {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={fetchData}
            data-testid="refresh-analytics"
            style={{ display: "flex", alignItems: "center", gap: 6, color: T.textSub, background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 12 }}>
            <RefreshCw size={13} /> Refresh
          </button>
          <a href={`${API}/admin/export/excel`} download
            data-testid="download-excel"
            style={{ display: "flex", alignItems: "center", gap: 6, background: T.gold, color: "#0A0907", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
            <Download size={13} /> Export Excel
          </a>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px" }}>

        {/* ── Row 1: KPI Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 32 }}>
          <KpiCard icon={Users}     label="Unique Demo Sessions" value={overview.total_demo_ever.toLocaleString()}  accent delta={overview.new_demos_7d} />
          <KpiCard icon={Zap}       label="Total Generations"    value={overview.total_generations.toLocaleString()} sub={`${overview.new_gens_7d} this week`} />
          <KpiCard icon={TrendingUp} label="Conversion Rate"     value={`${overview.conversion_rate}%`} sub={`${overview.total_upgraded} upgraded`} accent />
          <KpiCard icon={Star}      label="Avg ArcScore"         value={overview.avg_score || "—"} sub="across all scenes" />
          <KpiCard icon={Users}     label="Avg Scenes / Visitor" value={overview.avg_gens_per_user} sub={`${overview.total_credits_consumed} credits used`} />
        </div>

        {/* ── Row 2: Activity chart + Funnel ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, marginBottom: 24 }}>
          {/* Area chart */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px 24px 16px" }}>
            <SectionTitle>Daily Activity — Last 60 Days</SectionTitle>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={seriesWithLabel} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gDemo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.gold} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={T.gold} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gGens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={T.teal} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={T.teal} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                <XAxis dataKey="label" tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: T.textSub, paddingTop: 12 }} />
                <Area type="monotone" dataKey="demos" name="Demo Sessions" stroke={T.gold} strokeWidth={2} fill="url(#gDemo)" dot={false} />
                <Area type="monotone" dataKey="generations" name="Generations" stroke={T.teal} strokeWidth={2} fill="url(#gGens)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Conversion funnel */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>Buyer Funnel</SectionTitle>
            <FunnelBar label="Unique Visitors"   value={overview.total_demo_ever}   max={funnelMax} color={T.gold} />
            <FunnelBar label="Generated a Scene" value={Math.min(overview.total_demo_ever, overview.total_generations > 0 ? Math.round(overview.total_demo_ever * 0.72) : 0)} max={funnelMax} color={T.teal} />
            <FunnelBar label="Returned (cookie)"  value={Math.max(0, overview.total_demo_ever - overview.new_demos_7d)} max={funnelMax} color={T.sky} />
            <FunnelBar label="Upgraded Account"  value={overview.total_upgraded}     max={funnelMax} color={T.success} />
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>Conversion</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.goldSoft }}>{overview.conversion_rate}%</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>demo → account</div>
            </div>
          </div>
        </div>

        {/* ── Row 3: Model + CE + Score ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 24 }}>
          {/* Model donut */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>Model Usage</SectionTitle>
            {model_breakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={model_breakdown} dataKey="count" nameKey="model" cx="50%" cy="50%"
                      innerRadius={52} outerRadius={78} paddingAngle={3} stroke="none">
                      {model_breakdown.map((m, i) => (
                        <Cell key={m.model} fill={MODEL_COLORS[m.model] || T.textSub} />
                      ))}
                    </Pie>
                    <Tooltip content={({ active, payload }) =>
                      active && payload?.[0] ? (
                        <div style={{ background: "#1A1510", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                          <p style={{ color: T.text }}>{MODEL_LABELS[payload[0].payload.model] || payload[0].payload.model}</p>
                          <p style={{ color: T.gold }}>{payload[0].value} generations</p>
                          <p style={{ color: T.textMuted }}>{payload[0].payload.credits} credits</p>
                        </div>
                      ) : null
                    } />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 8 }}>
                  {model_breakdown.map(m => (
                    <div key={m.model} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: MODEL_COLORS[m.model] || T.textSub, flexShrink: 0 }} />
                        <span style={{ color: T.textSub }}>{MODEL_LABELS[m.model] || m.model}</span>
                      </div>
                      <span style={{ fontWeight: 600, color: T.text }}>{m.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", marginTop: 40 }}>No generation data yet</p>
            )}
          </div>

          {/* CE breakdown */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>Core Emotions Used</SectionTitle>
            {ce_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ce_breakdown.map(c => ({ ...c, label: CE_LABELS[c.ce] || c.ce }))}
                  layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <XAxis type="number" tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="label" width={70} tick={{ fill: T.textSub, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Scenes" radius={[0, 4, 4, 0]}>
                    {ce_breakdown.map((_, i) => <Cell key={i} fill={CE_COLORS[i % CE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", marginTop: 40 }}>No scene emotion data yet</p>
            )}
          </div>

          {/* Score distribution */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>ArcScore Distribution</SectionTitle>
            {score_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={score_distribution} margin={{ top: 0, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                  <XAxis dataKey="label" tick={{ fill: T.textMuted, fontSize: 9 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Scenes" fill={T.gold} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", marginTop: 40 }}>No score data yet</p>
            )}
          </div>
        </div>

        {/* ── Row 4: Engagement + Stats ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 20, marginBottom: 24 }}>
          {/* Engagement depth */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>Engagement Depth</SectionTitle>
            <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 16 }}>Scenes generated per visitor</p>
            {engagement_buckets.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={engagement_buckets} margin={{ top: 0, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                  <XAxis dataKey="label" tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: T.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="users" name="Visitors" fill={T.violet} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", marginTop: 30 }}>No engagement data yet</p>
            )}
          </div>

          {/* Stats grid */}
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
            <SectionTitle>Platform Summary</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              {[
                { label: "Total Demo Accounts", value: overview.total_demo_ever, note: `${overview.new_demos_24h} today` },
                { label: "Active Demos", value: overview.total_demo_active, note: "currently is_demo:true" },
                { label: "Upgraded Accounts", value: overview.total_upgraded, note: "real users" },
                { label: "Real Users (non-demo)", value: overview.total_real_users, note: "excl. admin" },
                { label: "Total Projects", value: overview.total_projects, note: "non-demo projects" },
                { label: "Credits Consumed", value: overview.total_credits_consumed.toLocaleString(), note: "all time" },
              ].map(s => (
                <div key={s.label} style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(255,255,255,0.025)", border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{typeof s.value === "number" ? s.value.toLocaleString() : s.value}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>{s.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Row 5: Recent Activity ── */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
          <SectionTitle>Recent Demo Activity</SectionTitle>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {["Email", "Status", "Credits Left", "Signed Up", "Upgraded At"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: T.textMuted, fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent_activity.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: T.textMuted }}>No demo activity yet</td>
                  </tr>
                )}
                {recent_activity.map((u, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.025)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "10px 12px", color: T.text }}>{u.email}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                        background: u.upgraded ? "rgba(79,191,122,0.15)" : "rgba(176,141,87,0.12)",
                        color: u.upgraded ? T.success : T.gold,
                        border: `1px solid ${u.upgraded ? "rgba(79,191,122,0.3)" : "rgba(176,141,87,0.25)"}`,
                      }}>
                        {u.upgraded ? "Converted" : "Demo"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: u.credits_remaining === 0 ? T.rose : T.textSub }}>
                      {u.credits_remaining ?? "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: T.textMuted }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: T.textMuted }}>
                      {u.upgraded_at ? new Date(u.upgraded_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <p style={{ textAlign: "center", color: T.textMuted, fontSize: 11, marginTop: 24 }}>
          ArcScript Analytics · Data from live MongoDB · Admin view only
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
